import { Link } from "react-router-dom";
import { 
  Shield, 
  FileText, 
  Check, 
  Mail, 
  Lock, 
  ArrowRight,
  TrendingUp, 
  Globe
} from "lucide-react";

export default function Footer() {
  return (
    <footer id="main-footer" className="bg-[#111827] text-gray-100 border-t-2 border-amber-500/30 pt-16 pb-8 relative overflow-hidden font-sans">
      {/* Decorative premium ambient glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Trust Badges Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-12 mb-12 border-b border-gray-800/80">
          <div className="flex items-center gap-3.5 bg-gray-900/60 p-4 rounded-xl border border-gray-800 hover:border-amber-500/30 transition-all duration-300 group">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Check className="w-4 h-4 text-emerald-400" /> Secure Website
              </p>
              <p className="text-xs text-gray-400 mt-0.5">256-bit SSL encrypted & secure data processing</p>
            </div>
          </div>

          <div className="flex items-center gap-3.5 bg-gray-900/60 p-4 rounded-xl border border-gray-800 hover:border-amber-500/30 transition-all duration-300 group">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Check className="w-4 h-4 text-amber-400" /> Free Financial Tools
              </p>
              <p className="text-xs text-gray-400 mt-0.5">100% free with no registration or paywalls</p>
            </div>
          </div>

          <div className="flex items-center gap-3.5 bg-gray-900/60 p-4 rounded-xl border border-gray-800 hover:border-amber-500/30 transition-all duration-300 group">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Check className="w-4 h-4 text-blue-400" /> Updated Regularly
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Calculations align with current 2026 guidelines</p>
            </div>
          </div>
        </div>

        {/* Main Columns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-left">
          
          {/* Column 1: Brand & Purpose */}
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center text-slate-900 font-extrabold text-sm tracking-tighter">
                UM
              </div>
              <span className="text-lg font-bold tracking-tight text-white">
                US Money <span className="text-amber-400">AI</span>
              </span>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed font-light">
              We empower consumers and households with clear, straightforward, and reliable planning calculators. Built to help you manage your money with complete confidence.
            </p>
          </div>

          {/* Column 2: Platform Menu */}
          <div className="space-y-4">
            <h4 className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest pb-1 border-b border-gray-800">
              PLATFORM NAVIGATION
            </h4>
            <ul className="space-y-2.5 text-sm font-light">
              <li>
                <Link to="/" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Dashboard Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  About USMoneyAI
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Contact Support
                </Link>
              </li>
              <li>
                <Link 
                  to="/#faq-section"
                  className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group"
                  onClick={() => {
                    setTimeout(() => {
                      const el = document.getElementById("faq-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }, 150);
                  }}
                >
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Frequently Asked Questions
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Category Highlights */}
          <div className="space-y-4">
            <h4 className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest pb-1 border-b border-gray-800">
              POPULAR CALCULATORS
            </h4>
            <ul className="space-y-2.5 text-sm font-light">
              <li>
                <Link to="/loan-calculator" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Mortgage Amortization
                </Link>
              </li>
              <li>
                <Link to="/debt-payoff-calculator" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Debt Payoff Tools
                </Link>
              </li>
              <li>
                <Link to="/compound-interest-calculator" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Compound Interest
                </Link>
              </li>
              <li>
                <Link to="/retirement-calculator" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Retirement Planning
                </Link>
              </li>
              <li>
                <Link to="/budget-planner" className="text-gray-300 hover:text-amber-400 transition-all duration-200 flex items-center gap-1.5 group">
                  <ArrowRight className="w-3.5 h-3.5 text-amber-500/40 group-hover:text-amber-400 transition-colors" />
                  Savings &amp; Budgets
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Quality & Credentials */}
          <div className="space-y-4">
            <h4 className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest pb-1 border-b border-gray-800">
              EDITORIAL VERIFICATION
            </h4>
            <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-800 space-y-3.5">
              <p className="text-xs text-gray-300 leading-relaxed font-light">
                We strive to provide accurate financial calculators and helpful educational content. Our tools are regularly reviewed and improved to ensure a better user experience.
              </p>
              <div className="flex flex-col gap-1.5 text-xs">
                <span className="text-gray-400 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5 text-amber-400 shrink-0" /> Support Desk:
                </span>
                <a 
                  href="mailto:Earningtab@72gmail.com"
                  className="text-amber-400 hover:text-amber-300 transition-colors font-medium underline"
                >
                  Earningtab@72gmail.com
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Financial advisory & regulatory disclosure box */}
        <div className="mt-12 p-6 bg-gray-950/70 rounded-xl border border-gray-800 text-left space-y-3.5">
          <h5 className="inline-flex items-center gap-1.5 font-semibold text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20 uppercase tracking-widest text-[10px] font-sans">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
            Financial Disclaimer
          </h5>
          <p className="text-xs text-gray-400 font-light leading-relaxed">
            The information, calculators, and resources provided on USMoneyAI are for educational and informational purposes only. They should not be considered financial, investment, tax, or legal advice.
          </p>
          <p className="text-xs text-gray-400 font-light leading-relaxed">
            USMoneyAI is an independent financial calculator website and does not provide professional financial services. Calculator results are estimates based on the information entered and may not reflect actual outcomes.
          </p>
          <p className="text-xs text-gray-400 font-light leading-relaxed">
            Before making important financial, investment, tax, or legal decisions, we recommend consulting a qualified professional.
          </p>
        </div>

        {/* Divider line and copyright row */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400 font-light text-center md:text-left">
            © 2026 US Money AI. All Rights Reserved. Smart calculation engines worldwide.
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-gray-400">
            <Link to="/about" className="hover:text-amber-400 transition-all duration-200 underline underline-offset-2">About Us</Link>
            <span className="text-gray-700">|</span>
            <Link to="/privacy" className="hover:text-amber-400 transition-all duration-200 underline underline-offset-2">Privacy Policy</Link>
            <span className="text-gray-700">|</span>
            <Link to="/disclaimer" className="hover:text-amber-400 transition-all duration-200 underline underline-offset-2">Disclaimer &amp; Disclosures</Link>
            <span className="text-gray-700">|</span>
            <Link to="/terms" className="hover:text-amber-400 transition-all duration-200 underline underline-offset-2">Terms of Service</Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
